import type { StoreApi } from "zustand/vanilla";

import { ApiError, type Api } from "../api/client";
import type { ClientMessage, CreateRoomIn, FilmCard, ServerMessage, StateMsg } from "../api/types";
import type { Platform } from "../lib/platform";
import { parseRoomInput, resolveRoomId } from "../lib/telegram";
import { inviteLink, kinopoiskUrl, shareUrl } from "../lib/url";
import type { SendResult, SocketStatus } from "../realtime/RoomSocket";
import { sameDeck } from "./deck";
import { defaultFilterForm, formToFilters, setYear, toggleValue } from "./filters";
import { createGameStore } from "./store";
import type { GameState, Session, WaitingKind } from "./types";

/** Часть RoomSocket, которая нужна игре (в тестах подменяется). */
export interface SocketLike {
  readonly status: SocketStatus;
  connect(): void;
  close(): void;
  send(message: ClientMessage): SendResult;
  onMessage(listener: (message: ServerMessage) => void): () => void;
  onStatusChange(listener: () => void): () => void;
}

export interface GameDeps {
  api: Pick<
    Api,
    | "filters"
    | "collections"
    | "createRoom"
    | "roomExists"
    | "soloDeck"
    | "soloCollectionDeck"
    | "checkSubscription"
  >;
  createSocket(roomId: string): SocketLike;
  platform: Platform;
  getInitData(): string | null;
  botLink: string;
  channelUrl: string;
  toastMs?: number;
}

const MSG = {
  network: "Нет связи с сервером. Проверьте интернет.",
  generic: "Что-то пошло не так. Попробуйте ещё раз.",
  login: "Не удалось войти. Откройте приложение заново через Telegram.",
  roomNotFound: "Комната не найдена или уже закрыта.",
  badCode: "Неверный код комнаты.",
  roomFull: "В этой комнате уже двое.",
  replaced: "Комната открыта на другом устройстве.",
  unreachable: "Не удаётся подключиться. Проверьте интернет.",
  notSubscribed: "Подписка не найдена. Подпишитесь на канал и попробуйте снова.",
  emptyFilters: "По таким фильтрам ничего не нашлось. Попробуйте изменить их.",
  emptyCollection: "В этой подборке пока нет фильмов.",
  badQr: "Не удалось распознать QR-код.",
  noScanner: "Сканирование QR в этом окружении не поддерживается.",
} as const;

const EMPTY_ROUND = {
  deck: [] as FilmCard[],
  index: 0,
  likes: [] as string[],
  matchId: null,
  otherProgress: null,
} satisfies Partial<GameState>;

export class GameController {
  readonly store: StoreApi<GameState> = createGameStore();

  readonly #deps: GameDeps;
  #socket: SocketLike | null = null;
  #unsubscribe: (() => void)[] = [];
  #toastTimer: ReturnType<typeof setTimeout> | null = null;
  /** Комната из ссылки-приглашения: подключимся к ней, как только пройдём проверки. */
  #pendingRoom: string | null = null;

  constructor(deps: GameDeps) {
    this.#deps = deps;
  }

  get state(): GameState {
    return this.store.getState();
  }

  get canScanQr(): boolean {
    return this.#deps.platform.canScanQr;
  }

  /** Ссылка-приглашение в текущую комнату (для QR и «Поделиться»). */
  get inviteLink(): string | null {
    const { session } = this.state;
    return session?.kind === "room" ? inviteLink(this.#deps.botLink, session.id) : null;
  }

  // ================================================================= запуск

  async init(): Promise<void> {
    if (!this.#deps.getInitData()) {
      this.#set({ screen: "no-telegram" });
      return;
    }
    const { platform } = this.#deps;
    this.#pendingRoom = resolveRoomId(platform.startParam, platform.search);
    this.#set({ screen: "loading", fatalMessage: null });
    await this.#bootstrap();
  }

  dispose(): void {
    this.#disconnect();
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
  }

  async #bootstrap(): Promise<void> {
    try {
      this.#set({ catalog: await this.#deps.api.filters() });
    } catch (error) {
      this.#onApiError(error, { fatal: true });
      return;
    }
    const room = this.#pendingRoom;
    this.#pendingRoom = null;
    this.#set({ screen: "start" });
    if (room) await this.joinRoom(room);
  }

  // ================================================================= подписка

  openChannel(): void {
    this.#deps.platform.openTelegramLink(this.#deps.channelUrl);
  }

  async recheckSubscription(): Promise<void> {
    if (this.state.busy) return;
    this.#set({ busy: true });
    try {
      if (await this.#deps.api.checkSubscription()) {
        this.#set({ busy: false });
        await this.#bootstrap();
        return;
      }
      this.toast(MSG.notSubscribed);
    } catch (error) {
      this.#onApiError(error);
    }
    this.#set({ busy: false });
  }

  // ================================================================= навигация

  goHome(): void {
    this.#disconnect();
    this.#set({ ...EMPTY_ROUND, session: null, waiting: "peer", screen: "start" });
  }

  openConnect(): void {
    this.#set({ screen: "connect" });
  }

  async openCollections(): Promise<void> {
    this.#set({ screen: "collections" });
    if (this.state.collections) return;
    try {
      this.#set({ collections: await this.#deps.api.collections() });
    } catch (error) {
      this.#set({ screen: "start" });
      this.#onApiError(error);
    }
  }

  // ================================================================= комнаты

  createFilterRoom(): Promise<void> {
    return this.#createRoom({ mode: "filters" });
  }

  pickCollection(collectionId: string): Promise<void> {
    return this.#createRoom({ mode: "collection", collection_id: collectionId });
  }

  async #createRoom(body: CreateRoomIn): Promise<void> {
    if (this.state.busy) return;
    this.#set({ busy: true });
    try {
      const id = await this.#deps.api.createRoom(body);
      this.#disconnect();
      this.#set({
        ...EMPTY_ROUND,
        filterForm: defaultFilterForm(),
        session: {
          kind: "room",
          id,
          mode: body.mode ?? "filters",
          collectionId: body.collection_id ?? null,
          isCreator: true,
        },
        screen: "invite",
      });
    } catch (error) {
      this.#onApiError(error);
    }
    this.#set({ busy: false });
  }

  /** Создатель нажал «Начать подбор» / «Погнали». */
  startRoom(): void {
    const { session } = this.state;
    if (session?.kind !== "room") return;
    this.#set({ waiting: "peer", screen: "waiting" });
    this.#connect(session.id);
  }

  share(): void {
    const { session } = this.state;
    const link = this.inviteLink;
    if (session?.kind === "room" && link) {
      this.#deps.platform.openTelegramLink(shareUrl(link, session.id));
    }
  }

  async joinFromInput(input: string): Promise<void> {
    const id = parseRoomInput(input);
    if (!id) {
      this.toast(MSG.badCode);
      return;
    }
    await this.joinRoom(id);
  }

  async scanQr(): Promise<void> {
    const { platform } = this.#deps;
    if (!platform.canScanQr) {
      this.toast(MSG.noScanner);
      return;
    }
    const text = await platform.scanQr();
    if (text === null) return;
    const id = parseRoomInput(text);
    if (id) await this.joinRoom(id);
    else this.toast(MSG.badQr);
  }

  async joinRoom(id: string): Promise<void> {
    if (this.state.busy) return;
    this.#set({ busy: true });
    try {
      if (!(await this.#deps.api.roomExists(id))) {
        this.toast(MSG.roomNotFound);
        return;
      }
    } catch (error) {
      this.#onApiError(error);
      return;
    } finally {
      this.#set({ busy: false });
    }
    this.#disconnect();
    this.#set({
      ...EMPTY_ROUND,
      filterForm: defaultFilterForm(),
      session: { kind: "room", id, mode: "filters", collectionId: null, isCreator: false },
      waiting: "peer",
      screen: "waiting",
    });
    this.#connect(id);
  }

  // ================================================================= одиночный режим

  /** «Смотреть одному» — из приглашения или с экрана «нет совпадений». */
  async watchSolo(): Promise<void> {
    const { session } = this.state;
    const collectionId = session?.collectionId ?? null;
    this.#disconnect();
    this.#set({
      ...EMPTY_ROUND,
      filterForm: defaultFilterForm(),
      session: { kind: "solo", collectionId },
    });
    if (collectionId) await this.#loadSoloCollection(collectionId);
    else this.#enterFilters();
  }

  async #loadSoloCollection(collectionId: string): Promise<void> {
    if (this.state.busy) return;
    this.#set({ busy: true });
    try {
      const deck = await this.#deps.api.soloCollectionDeck(collectionId);
      if (deck.length === 0) {
        this.toast(MSG.emptyCollection);
        this.#set({ screen: "start", session: null });
      } else {
        this.#startDeck(deck);
      }
    } catch (error) {
      this.#set({ screen: "start", session: null });
      this.#onApiError(error);
    }
    this.#set({ busy: false });
  }

  // ================================================================= фильтры

  toggleCountry(name: string): void {
    const { filterForm } = this.state;
    this.#set({
      filterForm: { ...filterForm, countries: toggleValue(filterForm.countries, name) },
    });
  }

  toggleGenre(name: string): void {
    const { filterForm } = this.state;
    this.#set({ filterForm: { ...filterForm, genres: toggleValue(filterForm.genres, name) } });
  }

  setRating(value: number): void {
    this.#set({ filterForm: { ...this.state.filterForm, ratingFrom: value } });
  }

  setYear(which: "from" | "to", value: number): void {
    this.#set({ filterForm: setYear(this.state.filterForm, which, value) });
  }

  async submitFilters(): Promise<void> {
    const { session, filterForm, busy } = this.state;
    if (busy) return;
    const filters = formToFilters(filterForm);

    if (session?.kind === "room") {
      this.#send({ type: "set_filters", filters });
      this.#set({ waiting: "filters", screen: "waiting" });
      return;
    }

    this.#set({ busy: true });
    try {
      const deck = await this.#deps.api.soloDeck(filters);
      if (deck.length === 0) this.toast(MSG.emptyFilters);
      else this.#startDeck(deck);
    } catch (error) {
      this.#onApiError(error);
    }
    this.#set({ busy: false });
  }

  #enterFilters(): void {
    this.#set({ screen: "filters" });
    // свежий каталог (на сервере он кэшируется); ошибка тут не критична
    this.#deps.api
      .filters()
      .then((catalog) => this.#set({ catalog }))
      .catch(() => undefined);
  }

  // ================================================================= свайпы

  vote(kind: "like" | "dislike"): void {
    const { session, deck, index, matchId, likes } = this.state;
    const card = deck[index];
    if (!session || !card || matchId) return;

    if (session.kind === "solo") {
      if (kind === "like") {
        this.#set({ matchId: card.id });
        return;
      }
      this.#advance(index + 1);
      return;
    }

    if (kind === "like") {
      this.#set({ likes: likes.includes(card.id) ? likes : [...likes, card.id] });
      this.#send({ type: "vote", card_id: card.id, vote: "like" });
    }
    this.#advance(index + 1);
    this.#send({ type: "progress", index: index + 1 });
  }

  #advance(next: number): void {
    const { session, deck } = this.state;
    if (next < deck.length) {
      this.#set({ index: next });
    } else if (session?.kind === "room") {
      this.#set({ index: next, waiting: "opponent", screen: "waiting" });
    } else {
      this.#set({ index: next, screen: "no-match" });
    }
  }

  openMovie(card: FilmCard): void {
    if (this.state.session?.kind === "room") {
      this.#send({ type: "watch_click", film_id: card.id });
    }
    this.#deps.platform.openLink(kinopoiskUrl(card));
  }

  /** «Попробовать ещё раз» на экране «нет совпадений». */
  async retry(): Promise<void> {
    const { session } = this.state;
    if (session?.kind === "room") {
      this.#set({ ...EMPTY_ROUND, waiting: "restart", screen: "waiting" });
      this.#send({ type: "restart" });
    } else if (session?.kind === "solo") {
      this.#set({ ...EMPTY_ROUND });
      if (session.collectionId) await this.#loadSoloCollection(session.collectionId);
      else this.#enterFilters();
    }
  }

  // ================================================================= сообщения сервера

  #onMessage(message: ServerMessage): void {
    switch (message.type) {
      case "state":
        this.#applyState(message);
        break;
      case "peer_joined":
        this.#set({ peerOnline: true });
        break;
      case "peer_left":
        this.#set({ peerOnline: false });
        break;
      case "await_filters":
        // новый раунд (или второй игрок только подключился): прошлая колода не нужна
        this.#set({ ...EMPTY_ROUND });
        this.#enterFilters();
        break;
      case "empty_deck":
        this.toast(MSG.emptyFilters);
        break;
      case "deck":
        this.#onDeck(message.deck);
        break;
      case "match":
        if (!this.state.matchId) this.#set({ matchId: message.card_id, screen: "swipe" });
        break;
      case "no_match":
        this.#set({ screen: "no-match" });
        break;
      case "other_progress":
        this.#set({ otherProgress: { index: message.index, total: message.total } });
        break;
      case "error":
        // фатальные ошибки приходят ещё и как закрытие сокета — см. #onSocketClosed
        break;
    }
  }

  #onDeck(deck: FilmCard[]): void {
    const state = this.state;
    if (state.session?.kind !== "room") return;
    // тот же раунд (дубликат снимка) не сбрасываем; после «нет совпадений» — новая колода
    if (sameDeck(state.deck, deck) && state.screen !== "no-match") return;
    this.#startDeck(deck);
  }

  /** Полный снимок комнаты: приходит после каждого (пере)подключения. Применяем идемпотентно. */
  #applyState(snapshot: StateMsg): void {
    const state = this.state;
    if (state.session?.kind !== "room") return;

    const session: Session = {
      kind: "room",
      id: state.session.id,
      mode: snapshot.mode,
      collectionId: snapshot.collection_id,
      isCreator: snapshot.is_creator,
    };
    const peerOnline = snapshot.peers_online > 0;

    if (!snapshot.deck) {
      // раунд не начат (или перезапущен, пока нас не было)
      const waiting: WaitingKind = snapshot.phase === "waiting_filters" ? "filters" : "peer";
      this.#set({
        ...EMPTY_ROUND,
        session,
        peerOnline,
        waiting,
        screen: snapshot.phase === "filters" ? "filters" : "waiting",
      });
      return;
    }

    const same = sameDeck(state.deck, snapshot.deck);
    const index = same ? Math.max(state.index, snapshot.my_index) : snapshot.my_index;
    const likes = same ? state.likes : snapshot.my_likes;
    const matchId = snapshot.matches[0] ?? (same ? state.matchId : null);

    let screen: GameState["screen"] = "swipe";
    if (snapshot.phase === "no_match") screen = "no-match";
    else if (!matchId && index >= snapshot.deck.length) screen = "waiting";

    this.#set({
      session,
      peerOnline,
      deck: snapshot.deck,
      index,
      likes,
      matchId,
      otherProgress: { index: snapshot.other_index, total: snapshot.deck.length },
      waiting: "opponent",
      screen,
    });

    // Сверка: досылаем то, о чём сервер мог не узнать из-за обрыва (голос идемпотентен).
    for (const id of likes) {
      if (!snapshot.my_likes.includes(id)) this.#send({ type: "vote", card_id: id, vote: "like" });
    }
    if (index !== snapshot.my_index) this.#send({ type: "progress", index });
  }

  #startDeck(deck: FilmCard[]): void {
    this.#set({
      ...EMPTY_ROUND,
      deck,
      otherProgress: this.state.session?.kind === "room" ? { index: 0, total: deck.length } : null,
      screen: "swipe",
    });
  }

  // ================================================================= сокет

  #connect(roomId: string): void {
    this.#disconnect();
    const socket = this.#deps.createSocket(roomId);
    this.#socket = socket;
    this.#unsubscribe = [
      socket.onMessage((message) => this.#onMessage(message)),
      socket.onStatusChange(() => this.#onStatus(socket)),
    ];
    socket.connect();
  }

  #disconnect(): void {
    this.#unsubscribe.forEach((off) => off());
    this.#unsubscribe = [];
    this.#socket?.close();
    this.#socket = null;
    this.#set({ connection: null, peerOnline: false });
  }

  #send(message: ClientMessage): void {
    this.#socket?.send(message);
  }

  #onStatus(socket: SocketLike): void {
    if (socket !== this.#socket) return;
    const { status } = socket;
    this.#set({ connection: status });
    if (status.state !== "closed" || status.reason === "CLOSED_BY_USER") return;

    const roomId = this.state.session?.kind === "room" ? this.state.session.id : null;
    this.#disconnect();
    switch (status.reason) {
      case "NOT_SUBSCRIBED":
        this.#pendingRoom = roomId;
        this.#set({ screen: "subscribe" });
        return;
      case "INVALID_INIT_DATA":
        this.#set({ screen: "fatal", fatalMessage: MSG.login });
        return;
      case "ROOM_NOT_FOUND":
        this.toast(MSG.roomNotFound);
        break;
      case "ROOM_FULL":
        this.toast(MSG.roomFull);
        break;
      case "REPLACED":
        this.toast(MSG.replaced);
        break;
      default:
        this.toast(MSG.unreachable);
    }
    this.goHome();
  }

  // ================================================================= служебное

  toast(message: string): void {
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    this.#set({ toast: message });
    this.#toastTimer = setTimeout(() => this.dismissToast(), this.#deps.toastMs ?? 4000);
  }

  dismissToast(): void {
    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    this.#toastTimer = null;
    this.#set({ toast: null });
  }

  #onApiError(error: unknown, options: { fatal?: boolean } = {}): void {
    if (error instanceof ApiError) {
      if (error.isNotSubscribed) {
        this.#set({ screen: "subscribe" });
        return;
      }
      if (error.status === 401) {
        this.#set({ screen: "fatal", fatalMessage: MSG.login });
        return;
      }
    }
    const message =
      error instanceof ApiError && error.code === "NETWORK" ? MSG.network : MSG.generic;
    if (options.fatal) this.#set({ screen: "fatal", fatalMessage: message });
    else this.toast(message);
  }

  #set(partial: Partial<GameState>): void {
    this.store.setState(partial);
  }
}
